/**
 * BookingsCard — KPI row → fill bar → tabbed list on the workshop detail page
 * (BON-199, restructured BON-490). Sits in the LEFT column directly under
 * Basics/Schedule.
 *
 * Sources `WorkshopDetail.roster` / `.reservations` / `.treeReplacementPickups`
 * — all ops-DB, separate from the warehouse-derived top-level fill fields on
 * `Workshop`. Four counts sit above the fill bar: the three seat-consuming
 * states (bookings / reservations / in-checkout) and tree replacements, which
 * take no seat and are fenced off to the right, shown only when there is at
 * least one (AC4/AC7). Hovering a seat KPI highlights its fill-bar lane
 * (AC5); clicking a Bookings/Reservations/Tree-replacements KPI selects the
 * matching tab below (AC6). One list shows at a time: Bookings (the existing
 * roster rows), Reservations (the existing `WorkshopReservations`), or Tree
 * replacements, where staff who may manage them can add one and mark pickups.
 */
import { useState } from 'react';

import type {
  WorkshopReservation,
  WorkshopRoster,
  WorkshopTreeReplacement,
} from '../../../../api/workshops';
import { usePermissions } from '../../../../hooks/usePermissions';
import { MOBILE_MAX, useMediaQuery } from '../../../../lib/useMediaQuery';
import { Permission } from '../../../../types/permissions';
import type { FillSegment } from '../../primitives';

import { BookingsCardHeader } from './BookingsCardHeader';
import type { BookingsPacing, TabId } from './bookingsCardTypes';
import { BookingsFillBarSection } from './BookingsFillBarSection';
import { BookingsKpiRow } from './BookingsKpiRow';
import { BookingsTabBody } from './BookingsTabBody';
import { BookingsTabs } from './BookingsTabs';
import { isCheckInOpen } from './checkInModel';
import { treesToHandOut } from './tree-replacement-form';

export type { BookingsPacing } from './bookingsCardTypes';

interface Props {
  roster: WorkshopRoster;
  /** Pacing inputs (BON-197 R1). Undefined → no pill. */
  pacing?: BookingsPacing;
  /** Open the check-in modal (page owns the modal + `?checkin=1` URL state).
   *  Omitted on surfaces with no check-in affordance (e.g. read-only Teacher). */
  onOpenCheckIn?: () => void;
  /** BON-478 — reservations holding seats on this workshop. Their seats are
   *  already counted in `roster.reservedSeats`, from the same server snapshot. */
  reservations: WorkshopReservation[];
  /** BON-490 — active tree-replacement pickups, oldest booked first. Defaulted
   *  so version skew during a deploy cannot white-screen the page (same
   *  precedent as `reservations`). */
  treeReplacementPickups?: WorkshopTreeReplacement[];
  workshopId: string;
  canManageTreeReplacements?: boolean;
  venueTimeZone?: string | null;
}

/**
 * The seat lanes, in bar order (BON-478). "In checkout" is the REMAINDER lane, so
 * it also absorbs orphaned holds and any future `origin` — right arithmetic, since
 * the segments must sum to the seats held.
 */
function seatSegments(roster: WorkshopRoster): FillSegment[] {
  return [
    { label: 'booked', value: roster.confirmedAttendees, tone: 'booked' },
    { label: 'reserved · staff-created', value: roster.reservedSeats, tone: 'reserved' },
    { label: 'in checkout', value: roster.inCheckoutSeats, tone: 'in-checkout' },
  ];
}

export function BookingsCard({
  roster,
  pacing,
  onOpenCheckIn,
  reservations,
  treeReplacementPickups = [],
  workshopId,
  canManageTreeReplacements = false,
  venueTimeZone = null,
}: Props) {
  const now = new Date();
  // /bookings is route-gated on the same permission; an instructor sees the
  // section but must not get a link that dead-ends on an unauthorized page.
  const { hasAnyPermission } = usePermissions();
  const canManage = hasAnyPermission(Permission.READ_BOOKINGS, Permission.READ_ALL);

  // Responsive tiers (BON-490): mobile → 2-up KPI grid + horizontally-scrolling
  // tabs. Same mechanism the rest of this surface already uses (BON-478
  // CheckInModal, DashboardsShell). The lists themselves always flow with the
  // page rather than scrolling in their own box.
  const isMobile = useMediaQuery(MOBILE_MAX);

  const treeCount = treesToHandOut(treeReplacementPickups);

  const [activeTab, setActiveTab] = useState<TabId>('bookings');
  const [highlightTone, setHighlightTone] = useState<FillSegment['tone'] | null>(null);

  return (
    <section className="nb-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <BookingsCardHeader
        pacing={pacing}
        now={now}
        onOpenCheckIn={onOpenCheckIn}
        addTreeReplacement={
          canManageTreeReplacements
            ? { workshopId, onOpen: () => setActiveTab('tree-replacements') }
            : undefined
        }
      />

      <BookingsKpiRow
        bookedSeats={roster.confirmedAttendees}
        reservedSeats={roster.reservedSeats}
        inCheckoutSeats={roster.inCheckoutSeats}
        treeReplacementCount={treeCount}
        activeTab={activeTab}
        highlightTone={highlightTone}
        onSelectTab={setActiveTab}
        onHoverTone={setHighlightTone}
        isMobile={isMobile}
      />

      <BookingsFillBarSection
        segments={seatSegments(roster)}
        capacity={roster.capacity}
        highlightTone={highlightTone}
        occupiedSeats={roster.occupiedSeats}
        seatsOpen={roster.seatsOpen}
        showCheckedIn={isCheckInOpen(roster.checkInOpensAt) || roster.checkedInSeats > 0}
        checkedInSeats={roster.checkedInSeats}
        confirmedAttendees={roster.confirmedAttendees}
      />

      <BookingsTabs
        activeTab={activeTab}
        onSelectTab={setActiveTab}
        bookingsSeats={roster.confirmedAttendees}
        reservationsSeats={roster.reservedSeats}
        treeReplacementCount={treeCount}
        isMobile={isMobile}
      />

      <BookingsTabBody
        tab={activeTab}
        roster={roster}
        reservations={reservations}
        treeReplacements={{
          workshopId,
          pickups: treeReplacementPickups,
          canManage: canManageTreeReplacements,
          venueTimeZone,
        }}
        canManage={canManage}
      />
    </section>
  );
}
